'use client';

import React, { useState, useEffect, useMemo } from 'react';
import { format, parseISO, isWithinInterval, subDays, startOfDay, endOfDay } from 'date-fns';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from '@/components/ui/sheet';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Calendar } from '@/components/ui/calendar';
import {
  Filter,
  Search,
  ArrowUp,
  ArrowDown,
  ChevronLeft,
  ChevronRight,
  CalendarIcon,
  X,
} from 'lucide-react';
import {
  FilterableEntity,
  PhishingCampaignEntity,
  TrainingCourseEntity,
  AnnouncementCampaignEntity,
  AdvancedFilterDrawerProps,
  EntityStatus,
  DateRange,
  AdvancedFilters,
  SortField,
  SortOrder,
} from '@/lib/teams-portal/types';

// Status Filter Component
interface StatusFilterProps {
  value: EntityStatus[];
  onChange: (value: EntityStatus[]) => void;
}

function StatusFilter({ value, onChange }: StatusFilterProps) {
  const [open, setOpen] = useState(false);
  const allStatuses: EntityStatus[] = ['Completed', 'Ongoing', 'Scheduled', 'Draft'];
  const isAllSelected = value.length === allStatuses.length;
  const displayText = isAllSelected ? 'All Statuses' : `${value.length} selected`;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2">
          <Filter className="h-4 w-4" />
          Status: {displayText}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64">
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="font-medium text-sm">Status</h4>
            <div className="flex gap-2">
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs"
                onClick={() => onChange(allStatuses)}
              >
                All
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs"
                onClick={() => onChange([])}
              >
                None
              </Button>
            </div>
          </div>
          <Separator />
          <div className="space-y-2">
            {allStatuses.map((status) => (
              <div key={status} className="flex items-center space-x-2">
                <Checkbox
                  id={status}
                  checked={value.includes(status)}
                  onCheckedChange={(checked) => {
                    if (checked) {
                      onChange([...value, status]);
                    } else {
                      onChange(value.filter((s) => s !== status));
                    }
                  }}
                />
                <label
                  htmlFor={status}
                  className="text-sm cursor-pointer flex-1"
                >
                  {status}
                </label>
              </div>
            ))}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

// Date Range Picker Component
interface DateRangePickerProps {
  label: string;
  value: DateRange | null;
  onChange: (value: DateRange | null) => void;
}

function DateRangePicker({ label, value, onChange }: DateRangePickerProps) {
  const [open, setOpen] = useState(false);

  const presets = [
    {
      label: 'Today',
      getValue: () => ({
        from: startOfDay(new Date()),
        to: endOfDay(new Date()),
      }),
    },
    {
      label: 'Last 7 Days',
      getValue: () => ({
        from: subDays(new Date(), 7),
        to: new Date(),
      }),
    },
    {
      label: 'Last 30 Days',
      getValue: () => ({
        from: subDays(new Date(), 30),
        to: new Date(),
      }),
    },
    {
      label: 'Last 90 Days',
      getValue: () => ({
        from: subDays(new Date(), 90),
        to: new Date(),
      }),
    },
  ];

  const displayText =
    value?.from && value?.to
      ? `${format(value.from, 'MMM d')} - ${format(value.to, 'MMM d, yyyy')}`
      : 'All Time';

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2">
          <CalendarIcon className="h-4 w-4" />
          {label}: {displayText}
          {value && (
            <X
              className="h-3 w-3 ml-1 hover:bg-muted rounded-sm"
              onClick={(e) => {
                e.stopPropagation();
                onChange(null);
              }}
            />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <div className="flex">
          <div className="border-r p-3 space-y-1">
            {presets.map((preset) => (
              <Button
                key={preset.label}
                variant="ghost"
                size="sm"
                className="w-full justify-start"
                onClick={() => {
                  onChange(preset.getValue());
                  setOpen(false);
                }}
              >
                {preset.label}
              </Button>
            ))}
            <Separator className="my-2" />
            <Button
              variant="ghost"
              size="sm"
              className="w-full justify-start text-muted-foreground"
              onClick={() => {
                onChange(null);
                setOpen(false);
              }}
            >
              Clear Filter
            </Button>
          </div>
          <div className="p-3">
            <Calendar
              mode="range"
              selected={value || undefined}
              onSelect={(range) => onChange((range as DateRange | undefined) ?? null)}
              numberOfMonths={2}
            />
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export function AdvancedFilterDrawer({
  open,
  onOpenChange,
  entityType,
  entityLabel,
  entityLabelSingular,
  items,
  selectedIds,
  onApply,
}: AdvancedFilterDrawerProps) {
  // State management
  const [tempSelectedIds, setTempSelectedIds] = useState<Set<string>>(new Set());
  const [filters, setFilters] = useState<AdvancedFilters>({
    statuses: ['Completed', 'Ongoing', 'Scheduled', 'Draft'],
    createdDateRange: null,
    lastUpdatedDateRange: null,
    searchQuery: '',
  });
  const [sortField, setSortField] = useState<SortField>('lastUpdated');
  const [sortOrder, setSortOrder] = useState<SortOrder>('DESC');
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 25 });

  // Initialize tempSelectedIds when drawer opens
  useEffect(() => {
    if (open) {
      setTempSelectedIds(new Set(selectedIds));
    }
  }, [open, selectedIds]);

  // Reset pagination when filters change
  useEffect(() => {
    setPagination(prev => ({ ...prev, pageIndex: 0 }));
  }, [filters, sortField, sortOrder]);

  // Filter and sort items
  const filteredAndSortedItems = useMemo(() => {
    let result = [...items];

    // Filter by status
    if (filters.statuses.length > 0 && filters.statuses.length < 4) {
      result = result.filter(item => filters.statuses.includes(item.status));
    }

    // Filter by created date range
    const createdRange = filters.createdDateRange;
    if (createdRange?.from && createdRange?.to) {
      result = result.filter(item => {
        if (!item.createdAt || typeof item.createdAt !== 'string') return false;
        try {
          const itemDate = parseISO(item.createdAt);
          return isWithinInterval(itemDate, {
            start: startOfDay(createdRange.from!),
            end: endOfDay(createdRange.to!),
          });
        } catch (e) {
          return false;
        }
      });
    }

    // Filter by last updated date range
    const lastUpdatedRange = filters.lastUpdatedDateRange;
    if (lastUpdatedRange?.from && lastUpdatedRange?.to) {
      result = result.filter(item => {
        if (!item.lastUpdated || typeof item.lastUpdated !== 'string') return false;
        try {
          const itemDate = parseISO(item.lastUpdated);
          return isWithinInterval(itemDate, {
            start: startOfDay(lastUpdatedRange.from!),
            end: endOfDay(lastUpdatedRange.to!),
          });
        } catch (e) {
          return false;
        }
      });
    }

    // Filter by search query
    if (filters.searchQuery) {
      const query = filters.searchQuery.toLowerCase();
      result = result.filter(item =>
        item.name.toLowerCase().includes(query)
      );
    }

    // Sort
    result.sort((a, b) => {
      let aVal: any = a[sortField];
      let bVal: any = b[sortField];

      // For date fields, convert to Date objects for comparison
      if (sortField === 'createdAt' || sortField === 'lastUpdated') {
        aVal = new Date(aVal).getTime();
        bVal = new Date(bVal).getTime();
      } else if (sortField === 'name') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }

      if (sortOrder === 'ASC') {
        return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
      } else {
        return aVal < bVal ? 1 : aVal > bVal ? -1 : 0;
      }
    });

    return result;
  }, [items, filters, sortField, sortOrder]);

  // Paginate items
  const paginatedItems = useMemo(() => {
    const start = pagination.pageIndex * pagination.pageSize;
    const end = start + pagination.pageSize;
    return filteredAndSortedItems.slice(start, end);
  }, [filteredAndSortedItems, pagination]);

  const totalPages = Math.ceil(filteredAndSortedItems.length / pagination.pageSize);
  const selectedCount = tempSelectedIds.size;

  // Handlers
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortField(field);
      setSortOrder(field === 'lastUpdated' ? 'DESC' : 'ASC');
    }
  };

  const handleSelectAll = () => {
    const currentPageIds = paginatedItems.map(item => item.id);
    const allSelected = currentPageIds.every(id => tempSelectedIds.has(id));

    const newSet = new Set(tempSelectedIds);
    if (allSelected) {
      currentPageIds.forEach(id => newSet.delete(id));
    } else {
      currentPageIds.forEach(id => newSet.add(id));
    }
    setTempSelectedIds(newSet);
  };

  const handleToggleItem = (id: string) => {
    const newSet = new Set(tempSelectedIds);
    if (newSet.has(id)) {
      newSet.delete(id);
    } else {
      newSet.add(id);
    }
    setTempSelectedIds(newSet);
  };

  const handleClearAll = () => {
    setTempSelectedIds(new Set());
  };

  const handleApply = () => {
    onApply(Array.from(tempSelectedIds));
    onOpenChange(false);
  };

  const handleCancel = () => {
    onOpenChange(false);
  };

  // Status badge color mapping
  const getStatusBadgeVariant = (status: EntityStatus): 'default' | 'secondary' | 'outline' => {
    switch (status) {
      case 'Completed':
        return 'default'; // Will style as green
      case 'Ongoing':
        return 'default'; // Will style as blue
      case 'Scheduled':
        return 'secondary'; // Will style as yellow
      case 'Draft':
        return 'outline'; // Will style as gray
      default:
        return 'default';
    }
  };

  const getStatusColor = (status: EntityStatus): string => {
    switch (status) {
      case 'Completed':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'Ongoing':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'Scheduled':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'Draft':
        return 'bg-muted text-muted-foreground border-border';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  // Check if all items on current page are selected
  const allCurrentPageSelected = paginatedItems.length > 0 &&
    paginatedItems.every(item => tempSelectedIds.has(item.id));

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-[60vw] overflow-y-auto p-0 flex flex-col">
        {/* Header */}
        <SheetHeader className="px-6 py-4 border-b">
          <SheetTitle>Filter {entityLabel}</SheetTitle>
        </SheetHeader>

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {/* Filter Controls */}
          <div className="flex items-center gap-3 flex-wrap">
            {/* Status Filter */}
            <StatusFilter
              value={filters.statuses}
              onChange={(statuses) => setFilters({ ...filters, statuses })}
            />

            {/* Created Date Range */}
            <DateRangePicker
              label="Created"
              value={filters.createdDateRange}
              onChange={(range) => setFilters({ ...filters, createdDateRange: range })}
            />

            {/* Last Updated Date Range */}
            <DateRangePicker
              label="Last Updated"
              value={filters.lastUpdatedDateRange}
              onChange={(range) => setFilters({ ...filters, lastUpdatedDateRange: range })}
            />
          </div>

          {/* Search and Sort Controls */}
          <div className="flex items-center gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by name..."
                value={filters.searchQuery}
                onChange={(e) => setFilters({ ...filters, searchQuery: e.target.value })}
                className="pl-9"
              />
            </div>
            <Select
              value={sortField}
              onValueChange={(value) => setSortField(value as SortField)}
            >
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="lastUpdated">Last Updated</SelectItem>
                <SelectItem value="createdAt">Created Date</SelectItem>
                <SelectItem value="name">Name</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Results count */}
          <div className="text-sm text-muted-foreground">
            {filteredAndSortedItems.length} {filteredAndSortedItems.length === 1 ? 'result' : 'results'}
          </div>

          {/* Table */}
          <div className="border border-border rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead className="w-12">
                    <Checkbox
                      checked={allCurrentPageSelected && paginatedItems.length > 0}
                      onCheckedChange={handleSelectAll}
                      aria-label="Select all on page"
                    />
                  </TableHead>
                  <TableHead className="cursor-pointer hover:bg-muted" onClick={() => handleSort('name')}>
                    <div className="flex items-center gap-2">
                      Name
                      {sortField === 'name' && (
                        sortOrder === 'ASC' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      )}
                    </div>
                  </TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Creator</TableHead>
                  <TableHead className="cursor-pointer hover:bg-muted" onClick={() => handleSort('lastUpdated')}>
                    <div className="flex items-center gap-2">
                      Last Updated
                      {sortField === 'lastUpdated' && (
                        sortOrder === 'ASC' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      )}
                    </div>
                  </TableHead>
                  <TableHead className="cursor-pointer hover:bg-muted" onClick={() => handleSort('createdAt')}>
                    <div className="flex items-center gap-2">
                      Created
                      {sortField === 'createdAt' && (
                        sortOrder === 'ASC' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      )}
                    </div>
                  </TableHead>
                  {/* Entity-specific columns */}
                  {entityType === 'campaign' && (
                    <>
                      <TableHead className="text-right">Sent</TableHead>
                      <TableHead className="text-right">Opened</TableHead>
                      <TableHead className="text-right">Clicked</TableHead>
                    </>
                  )}
                  {entityType === 'training' && (
                    <>
                      <TableHead className="text-right">Assigned</TableHead>
                      <TableHead className="text-right">Completed</TableHead>
                    </>
                  )}
                  {entityType === 'announcement' && (
                    <>
                      <TableHead className="text-right">Sent</TableHead>
                      <TableHead className="text-right">Acknowledged</TableHead>
                    </>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedItems.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={entityType === 'campaign' ? 9 : 8} className="text-center py-8 text-muted-foreground">
                      No {entityLabel.toLowerCase()} found
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedItems.map((item) => (
                    <TableRow key={item.id} className="hover:bg-muted/50">
                      <TableCell>
                        <Checkbox
                          checked={tempSelectedIds.has(item.id)}
                          onCheckedChange={() => handleToggleItem(item.id)}
                          aria-label={`Select ${item.name}`}
                        />
                      </TableCell>
                      <TableCell className="font-medium text-foreground">{item.name}</TableCell>
                      <TableCell>
                        <Badge className={getStatusColor(item.status)}>
                          {item.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-muted-foreground">{item.creator}</TableCell>
                      <TableCell className="text-muted-foreground">
                        {item.lastUpdated && typeof item.lastUpdated === 'string' ? format(parseISO(item.lastUpdated), 'MMM d, yyyy') : 'N/A'}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {item.createdAt && typeof item.createdAt === 'string' ? format(parseISO(item.createdAt), 'MMM d, yyyy') : 'N/A'}
                      </TableCell>
                      {/* Entity-specific columns */}
                      {entityType === 'campaign' && (
                        <>
                          <TableCell className="text-right text-muted-foreground">
                            {(item as PhishingCampaignEntity).sent}
                          </TableCell>
                          <TableCell className="text-right text-muted-foreground">
                            {(item as PhishingCampaignEntity).opened}
                          </TableCell>
                          <TableCell className="text-right text-muted-foreground">
                            {(item as PhishingCampaignEntity).clicked}
                          </TableCell>
                        </>
                      )}
                      {entityType === 'training' && (
                        <>
                          <TableCell className="text-right text-muted-foreground">
                            {(item as TrainingCourseEntity).assigned}
                          </TableCell>
                          <TableCell className="text-right text-muted-foreground">
                            {(item as TrainingCourseEntity).completed}
                          </TableCell>
                        </>
                      )}
                      {entityType === 'announcement' && (
                        <>
                          <TableCell className="text-right text-muted-foreground">
                            {(item as AnnouncementCampaignEntity).sent}
                          </TableCell>
                          <TableCell className="text-right text-muted-foreground">
                            {(item as AnnouncementCampaignEntity).acknowledged}
                          </TableCell>
                        </>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {filteredAndSortedItems.length > 0 && (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Rows per page:</span>
                <Select
                  value={pagination.pageSize.toString()}
                  onValueChange={(value) =>
                    setPagination({ ...pagination, pageSize: parseInt(value), pageIndex: 0 })
                  }
                >
                  <SelectTrigger className="w-[80px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="25">25</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-4">
                <span className="text-sm text-muted-foreground">
                  {pagination.pageIndex * pagination.pageSize + 1}-
                  {Math.min((pagination.pageIndex + 1) * pagination.pageSize, filteredAndSortedItems.length)} of{' '}
                  {filteredAndSortedItems.length}
                </span>
                <div className="flex items-center gap-1">
                  <Button
                    variant="outline"
                    size="icon"
                    aria-label="Previous page"
                    onClick={() => setPagination({ ...pagination, pageIndex: pagination.pageIndex - 1 })}
                    disabled={pagination.pageIndex === 0}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    aria-label="Next page"
                    onClick={() => setPagination({ ...pagination, pageIndex: pagination.pageIndex + 1 })}
                    disabled={pagination.pageIndex >= totalPages - 1}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <SheetFooter className="px-6 py-4 border-t flex-row justify-between items-center">
          <div className="text-sm text-muted-foreground">
            {selectedCount} {selectedCount === 1 ? 'item' : 'items'} selected
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={handleClearAll}>
              Clear All
            </Button>
            <Button
              className=""
              onClick={handleApply}
            >
              Apply
            </Button>
          </div>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
